import Loader from 'components/Loader';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo } from 'react';
import { useLazyGetTopPayersByRevenueReportQuery } from 'store/api/reportingExecutive';
import { PartyRevenueTotals, TopPayersByRevenueRow } from 'types/reporting/executive';
import { ReportPage } from 'types/reporting/reportPage';
import { useClassifiers } from 'utils/useClassifiers';
import { clientFilter } from '../reportFilters';
import { useReportBaseCurrency } from '../reportCurrency';
import { ReportLayout } from '../ReportLayout';
import { toneLegendOf } from '../ReportToneLegend';
import { ReportRowLimitNote } from '../ReportRowLimitNote';
import { ReportEntry } from '../reportCatalog';
import {
    buildPartyRevenueColumns,
    buildPartyRevenueFilterConfig,
    PARTY_REVENUE_CLASSIFIERS,
    PartyColumns,
} from './partyRevenueTable';

type Row = TopPayersByRevenueRow;

const REPORT_KEY = 'top-payers-by-revenue';

const PAYER_COLUMNS: PartyColumns<Row> = {
    numberId: 'payerNumber',
    numberLabelId: 'reporting.column.payer-number',
    nameId: 'payerName',
    nameLabelId: 'reporting.column.payer-name',
    partyId: (row) => row.payerId,
    partyNumber: (row) => row.payerNumber,
    partyName: (row) => row.payerName,
};

export function TopPayersByRevenueReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetTopPayersByRevenueReportQuery();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: PARTY_REVENUE_CLASSIFIERS });
    const filterConfig = useMemo(
        () =>
            buildPartyRevenueFilterConfig(
                clientFilter('payerId', 'reporting.filter.payer'),
                ['payerNumber', 'payerName'],
                classifiers
            ),
        [classifiers]
    );
    const currency = useReportBaseCurrency(data?.totals?.baseCurrency ?? data?.content[0]?.baseCurrency);
    const columns = useMemo(() => buildPartyRevenueColumns(data?.content, PAYER_COLUMNS, currency), [data, currency]);
    return (
        <ReportLayout report={report} fillHeight toneLegend={toneLegendOf(columns)} baseCurrency={currency}>
            {isLoadingClassifiers ? (
                <Loader />
            ) : (
                <ListTableWithFilters<Row, ReportPage<Row, PartyRevenueTotals>>
                    data={data}
                    belowQuickFilters={<ReportRowLimitNote report={report} rowCount={data?.content.length} />}
                    isLoading={isFetching}
                    isError={isError}
                    fetchData={fetchData}
                    filterConfig={filterConfig}
                    columns={columns}
                    columnConfigId={`report-${REPORT_KEY}`}
                    enableColumnPicker
                    enableColumnReorder
                    enableColumnResize
                    pluralTextId="reporting.payers"
                    singularTextId="reporting.payer"
                    noEntriesId="reporting.no-rows"
                    noEntriesBodyId="reporting.no-rows-body"
                />
            )}
        </ReportLayout>
    );
}
